import { router } from 'expo-router';
import { jwtDecode } from 'jwt-decode';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Platform, StyleSheet, View } from 'react-native';
import { OtpInput } from 'react-native-otp-entry';

import { Button, IconInfo, IconMail, IconRefresh, Pressable, Text, tokens } from '@fhs/ui';
import { formatPhoneNumber } from '@fhs/ui/src/components/form-text-inputs/masks';

import { useAuthRouteBase } from '../auth-route-base-context';
import { IdentifyActionSheet } from '../components/actions-sheets/identify-action-sheet';
import {
  useCreateOtp,
  useJwtIdentifier,
  useNavigateWithJwt,
  useResendOtp,
  useValidateOtp,
} from '../hooks';
import { AuthIdentifiers, IdentifyAuthJwt } from '../types';

const OTP_LENGTH = 6;

export function VerifyOtp() {
  const { paths } = useAuthRouteBase();
  const [showIdentifyPopup, setShowIdentifyPopup] = useState<boolean>(false);
  const { createOtp, otpLoading, otpError } = useCreateOtp();
  const onIdentifyChallenge = useCallback(() => setShowIdentifyPopup(true), [setShowIdentifyPopup]);
  const {
    identifyAuthJwts,
    validatingOtp,
    validateOtp,
    otpValidationError,
    setOtpValidationError,
  } = useValidateOtp({
    onIdentifyChallenge,
  });
  const { resendOtp, resendOtpLoading, resendCountdownMessage } = useResendOtp();
  const { getJwtIdentifier } = useJwtIdentifier();
  const { navigateWithJWT } = useNavigateWithJwt();

  const otpContainerRef = useRef<View>(null);

  useEffect(() => {
    if (otpError) {
      setShowIdentifyPopup(false);
      router.dismissTo(paths.signIn);
    }
  }, [otpError, paths.signIn]);

  // OtpInput renders one focusable Pressable per digit cell, so Tab stops on each
  // box before reaching the real (hidden) input. Strip them from the tab order and
  // the a11y tree on web. Cells re-render while typing, so reapply via observer.
  useEffect(() => {
    if (Platform.OS !== 'web') {
      return;
    }
    const node = otpContainerRef.current as unknown as HTMLElement | null;
    if (!node) {
      return;
    }
    const hideCellsFromTabOrder = () => {
      node.querySelectorAll('[data-testid="otp-input"]').forEach(cell => {
        cell.setAttribute('tabindex', '-1');
        cell.setAttribute('aria-hidden', 'true');
      });
    };
    hideCellsFromTabOrder();
    const observer = new MutationObserver(hideCellsFromTabOrder);
    observer.observe(node, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const attemptValidateCode = useCallback(
    async otpInput => {
      await validateOtp(otpInput);
    },
    [validateOtp]
  );

  const getIdentifierComponent = useCallback((loginIdentifier, identifierType) => {
    switch (identifierType) {
      case AuthIdentifiers.PHONE_NUMBER:
        //Format as phone
        return <Text weight="bold">{formatPhoneNumber(loginIdentifier)} </Text>;
      case AuthIdentifiers.EMAIL:
        return <Text weight="bold">{loginIdentifier} </Text>;
      case AuthIdentifiers.JWT:
        return (
          <Text weight="bold">
            {jwtDecode<IdentifyAuthJwt>(loginIdentifier).redactedIdentity.redactedEmail}
          </Text>
        );
      default: {
        return null;
      }
    }
  }, []);

  const getLinkComponent = useCallback(identifierType => {
    switch (identifierType) {
      case AuthIdentifiers.PHONE_NUMBER:
        return (
          <Pressable
            accessibilityRole="link"
            accessibilityLabel="Change Number"
            onPress={() => router.dismissTo(paths.signIn)}
          >
            <Text.Paragraph size="md" style={{ textDecorationLine: 'underline' }}>
              Change Number
            </Text.Paragraph>
          </Pressable>
        );
      case AuthIdentifiers.EMAIL:
      case AuthIdentifiers.JWT:
        return (
          <Pressable
            accessibilityRole="link"
            accessibilityLabel="Change Email"
            onPress={() => router.replace(paths.continueWithEmail)}
          >
            <Text.Paragraph size="md" style={{ textDecorationLine: 'underline' }}>
              Change Email
            </Text.Paragraph>
          </Pressable>
        );
      default: {
        return null;
      }
    }
  }, [paths.continueWithEmail, paths.signIn]);

  const { loginIdentifier, identifierType } = getJwtIdentifier();
  const resendOtpDisabledMemo = useMemo(
    () => validatingOtp || otpLoading || resendCountdownMessage !== '' || resendOtpLoading,
    [otpLoading, resendCountdownMessage, resendOtpLoading, validatingOtp]
  );
  const verifyWithEmailDisabledMemo = useMemo(
    () => validatingOtp || otpLoading || resendOtpLoading,
    [otpLoading, resendOtpLoading, validatingOtp]
  );
  return (
    <>
      <View style={{ gap: 12 }}>
        <Text.Heading type={'three'}>Verify With Code </Text.Heading>
        <Text>
          {'Enter the 6-digit code sent to you at '}
          {getIdentifierComponent(loginIdentifier, identifierType)}
        </Text>
        {getLinkComponent(identifierType)}
      </View>
      <View ref={otpContainerRef} style={styles.otpContainerView}>
        <OtpInput
          numberOfDigits={OTP_LENGTH}
          focusColor={tokens.colors.$blackOpacity30}
          onTextChange={() => {
            setOtpValidationError(false);
          }}
          autoFocus
          blurOnFilled
          disabled={validatingOtp}
          type="numeric"
          onFilled={attemptValidateCode}
          theme={{
            disabledPinCodeContainerStyle: styles.otpContainerDisable,
            containerStyle: styles.otpContainer,
            pinCodeContainerStyle: styles.inputBox,
            filledPinCodeContainerStyle: otpValidationError
              ? styles.erroredInputBox
              : styles.inputBox,
          }}
          textInputProps={{
            accessibilityLabel: 'One-Time Password',
          }}
          textProps={{
            allowFontScaling: false,
            style: styles.otpText,
          }}
        />
        {otpValidationError && (
          <Text style={styles.errorMessage}>The code you've entered is incorrect.</Text>
        )}
      </View>

      <View style={styles.buttonContainer}>
        <View style={{ flexDirection: 'row', gap: 24 }}>
          <Button
            style={styles.button}
            type={'outline'}
            size="sm"
            onPress={resendOtp}
            disabled={resendOtpDisabledMemo}
            loading={resendOtpLoading}
          >
            {!resendOtpLoading && (
              <Button.Icon>
                <IconRefresh style={styles.buttonContent} />
              </Button.Icon>
            )}
            <Button.Text
              style={[styles.buttonContent, resendOtpDisabledMemo && styles.buttonContentDisabled]}
            >
              Resend Code
            </Button.Text>
          </Button>
          {validatingOtp && <ActivityIndicator color={tokens.colors.$houseRed} />}
        </View>
        {resendCountdownMessage !== '' && <Text>{resendCountdownMessage}</Text>}
        <Button
          style={styles.button}
          type={'outline'}
          size="sm"
          onPress={() => {
            if (identifierType === AuthIdentifiers.JWT) {
              navigateWithJWT(paths.continueWithEmail);
            } else {
              router.replace(paths.continueWithEmail);
            }
          }}
          disabled={verifyWithEmailDisabledMemo}
        >
          {!resendOtpLoading && (
            <Button.Icon>
              {identifierType !== AuthIdentifiers.PHONE_NUMBER ? (
                <IconInfo style={styles.buttonContent} />
              ) : (
                <IconMail style={styles.buttonContent} />
              )}
            </Button.Icon>
          )}
          <Button.Text
            style={[
              styles.buttonContent,
              verifyWithEmailDisabledMemo && styles.buttonContentDisabled,
            ]}
          >
            {identifierType !== AuthIdentifiers.PHONE_NUMBER
              ? 'Not Your Email'
              : 'Verify With Email'}
          </Button.Text>
        </Button>
      </View>
      {showIdentifyPopup && (
        <IdentifyActionSheet
          authJwts={identifyAuthJwts}
          isVisible
          onAccountSelect={() => {
            setShowIdentifyPopup(false);
          }}
          onSelectNoneOfAbove={() => {
            navigateWithJWT(paths.continueWithEmail);
          }}
          onSelect={(jwt: string) => {
            createOtp({
              loginIdentifier: jwt,
              identifierType: AuthIdentifiers.JWT,
            });
          }}
        />
      )}
    </>
  );
}

const styles = StyleSheet.create({
  button: {
    alignItems: 'center',
    borderColor: tokens.colors.$blackOpacity75,
    paddingHorizontal: 16,
  },
  buttonContainer: {
    marginRight: 'auto',
    alignItems: 'flex-start',
    gap: 16,
  },
  buttonContent: {
    alignItems: 'center',
    color: tokens.colors.$black,
  },
  buttonContentDisabled: {
    color: tokens.colors.$disabledText,
  },
  inputBox: {
    width: 48,
    height: 48,
    textAlign: 'center',
    color: tokens.colors.$black,
    borderColor: tokens.colors.$blackOpacity30,
    borderWidth: 1,
    borderRadius: 4,
  },
  erroredInputBox: {
    borderColor: tokens.colors.$error,
  },
  errorMessage: {
    color: tokens.colors.$houseRed,
    fontSize: 12,
  },
  otpContainerView: {
    gap: 8,
    width: '100%',
    marginVertical: 28,
  },
  otpContainer: {
    width: 'auto',
    alignContent: 'center',
  },
  otpContainerDisable: {
    backgroundColor: tokens.colors.$blackOpacity04,
  },
  otpText: {
    fontFamily: 'Montserrat',
    fontSize: 14,
    fontStyle: 'normal',
    fontWeight: 400,
  },
});
